import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { adminApi } from '../api';
import { Modal } from '../components/Modal';
import { TextInput } from '../components/Field';

function UserRow({ user, index, tenantName, onToggle, onRemove }) {
  const roleLabel = user.role === 'super_admin' ? 'Super Admin' : `Admin \u00b7 ${tenantName(user.tenant_id)}`;
  return (
    <div data-testid={`user-row-${user.id}`} className={`flex items-center gap-4 p-4 ${index > 0 ? 'border-t border-paelbi-line' : ''}`}>
      <div className="flex-1">
        <p className="font-medium text-paelbi-ink">{user.name} <span className="text-xs text-paelbi-ink/50">{user.email}</span></p>
        <p className="text-xs text-paelbi-ink/50">{roleLabel} {'\u00b7'} {user.active ? 'Attivo' : 'Disattivato'}</p>
      </div>
      <button type="button" onClick={() => onToggle(user)} className="rounded-lg border border-paelbi-line px-3 py-1.5 text-xs">{user.active ? 'Disattiva' : 'Attiva'}</button>
      <button type="button" data-testid={`user-delete-${user.id}`} onClick={() => onRemove(user)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
    </div>
  );
}

function UserFormModal({ editing, tenants, saving, onChange, onClose, onSave }) {
  return (
    <Modal open={!!editing} onClose={onClose} title="Nuovo utente"
      footer={<>
        <button type="button" onClick={onClose} className="rounded-btn border border-paelbi-line px-4 py-2 text-sm">Annulla</button>
        <button type="button" data-testid="user-save-button" onClick={onSave} disabled={saving} className="rounded-btn bg-paelbi-brown px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Salvo...' : 'Crea'}</button>
      </>}>
      {editing && (
        <div className="space-y-4">
          <TextInput label="Nome" value={editing.name} onChange={(v) => onChange({ ...editing, name: v })} />
          <TextInput label="Email" value={editing.email} onChange={(v) => onChange({ ...editing, email: v })} />
          <TextInput label="Password" type="password" value={editing.password} onChange={(v) => onChange({ ...editing, password: v })} />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Ruolo</span>
            <select value={editing.role} onChange={(e) => onChange({ ...editing, role: e.target.value })} className="w-full rounded-lg border border-paelbi-line bg-white px-3 py-2 text-sm">
              <option value="tenant_admin">Admin struttura</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </label>
          {editing.role === 'tenant_admin' && (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-paelbi-ink/80">Struttura</span>
              <select value={editing.tenant_id || ''} onChange={(e) => onChange({ ...editing, tenant_id: e.target.value })} className="w-full rounded-lg border border-paelbi-line bg-white px-3 py-2 text-sm">
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.brand?.name} ({t.slug})</option>)}
              </select>
            </label>
          )}
        </div>
      )}
    </Modal>
  );
}

export function Users() {
  const [list, setList] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.listUsers().then(setList);
  useEffect(() => { load(); adminApi.listTenants().then(setTenants); }, []);

  const openNew = () => setEditing({ email: '', password: '', name: '', role: 'tenant_admin', tenant_id: tenants[0]?.id || '' });

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...editing };
      if (body.role === 'super_admin') body.tenant_id = null;
      await adminApi.createUser(body);
      setEditing(null); await load();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (u) => { await adminApi.updateUser(u.id, { active: !u.active }); load(); };
  const remove = async (u) => {
    if (!window.confirm('Eliminare utente?')) return;
    try { await adminApi.deleteUser(u.id); load(); } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const tenantName = (id) => tenants.find((t) => t.id === id)?.brand?.name || '\u2014';

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-3xl text-paelbi-ink">Utenti</h1><p className="text-paelbi-ink/60">Gestisci gli accessi al pannello.</p></div>
        <button type="button" data-testid="user-new-button" onClick={openNew} className="inline-flex items-center gap-2 rounded-btn bg-paelbi-brown px-4 py-2.5 text-sm font-semibold text-white hover:bg-paelbi-brown-dark"><Plus size={16} />Nuovo utente</button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-paelbi-line bg-white" data-testid="users-list">
        {list.map((u, i) => (
          <UserRow key={u.id} user={u} index={i} tenantName={tenantName} onToggle={toggleActive} onRemove={remove} />
        ))}
      </div>

      <UserFormModal editing={editing} tenants={tenants} saving={saving} onChange={setEditing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

export default Users;
